import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import moment from 'moment';

import { ActionDropdownComponent } from 'src/app/components/action-dropdown/action-dropdown.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { FEATURES } from 'src/app/constants';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { formatConversationContent, formatSlackContent, getWhereClausesFromFilters } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { Conversation } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';
interface Suggestion {
  any;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    SharedPipesModule,
    RouterModule,
    FilterButtonComponent,
  ],
  selector: 'app-knowledge-base-suggestions',
  templateUrl: './knowledge-base-suggestions.component.html',
  styleUrls: ['./knowledge-base-suggestions.component.scss'],
})
export class KnowledgeBaseSuggestionsComponent implements OnInit {
  featureEnabled = true;
  isLoading = true;
  ColumnMode = ColumnMode;

  userId: string;
  user?: { id: string; name: string };

  limit = 20;
  cursor: any;
  searchText?: string;
  suggestions?: [{ id: string }];

  sortField = 'createdAt';
  sortDesc = true;
  sortLabel = 'Newest';

  filters: SelectedFilterMap = new Map();
  filterOptions: FilterOption[] = [
    {
      label: 'Customer',
      id: 'customerId',
      icon: 'business',
      type: 'select',
    },
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      icon: 'ticket',
      type: 'select',
    },
    {
      label: 'Project',
      id: 'projectId',
      icon: 'folder-open',
      type: 'select',
    },
    {
      label: 'Source',
      id: 'sourceType',
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
      ],
    },
  ];

  fetchSuggestions = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest & { isExport?: boolean } = {}): Promise<FetchDataResponse<Partial<Suggestion>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      search: this.searchText || undefined,
    };

    const where = this.getWhereClauses();

    if (where.length) {
      options.where = where;
    }

    const result = (await this.apiService.postPromise('/knowledge-base/suggestions', options)) as any;

    return result;
  };

  suggestionsDataSource = createDataSource(this.fetchSuggestions);

  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private searchablePopoverService: SearchablePopoverService,
    private popoverCtrl: PopoverController,
    private msgService: MessageService,
    private tagsService: TagsService,
    private analyticsService: AnalyticsService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.KNOWLEDGE_BASE_SYNC);
    await this.getSuggestions();
  }

  formatContent(conversation: Conversation, maxLength: number = 125) {
    return formatConversationContent(conversation, maxLength);
  }

  getWhereClauses() {
    const filterWheres = getWhereClausesFromFilters(this.filters);

    if (this.authService.selectedProject) {
      filterWheres.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    return filterWheres;
  }

  async getSuggestions() {
    this.isLoading = true;
    try {
      // const { data } = await this.apiService.postPromise('/knowledge-base/suggestions', {}) as any;
      // this.suggestions = data;
      await this.suggestionsDataSource.firstPage();
    } finally {
      this.isLoading = false;
    }
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.getSuggestions();
  }

  getTagsForRow(row: any): string {
    return row.tags?.map((tag) => tag.name).join(', ');
  }

  displayDate(date: Date) {
    return moment(date).from(moment());
  }

  handleFilters() {
    this.getSuggestions();
  }

  async addTagToSuggestion(suggestionId: string, event: Event) {
    this.searchablePopoverService.tags({
      event,
      callback: async (tagId: string) => {
        this.analyticsService.trackEvent('Knowledge Base Suggestion', 'Add Tag');

        await this.tagsService.createConversationLinks(tagId, [suggestionId]);

        await this.suggestionsDataSource.reloadPage();
      },
    });
  }

  async editTagForSuggestion(suggestionId: string, tagId: string, event: Event) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: [
          {
            label: 'Remove tag',
            icon: 'close-circle',
          },
        ],
        callback: async (index: number, _label: string) => {
          popover.dismiss();

          if (index === 0) {
            await this.tagsService.deleteConversationLinks(tagId, [suggestionId]);
            await this.suggestionsDataSource.reloadPage();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async generateArticle(conversation) {
    this.router.navigate(['../articles/new'], {
      relativeTo: this.route,
      queryParams: { conversationId: conversation.id, from: 'suggestions' },
    });
  }

  async ignoreArticle(conversation) {
    const options = {
      ticketId: conversation.id,
    };

    try {
      const result = (await this.apiService.postPromise('/knowledge-base/suggestions/ignore', options)) as any;
    } catch (e) {
      this.msgService.showError(e);
    }

    await this.getSuggestions();
  }
}
